import React, { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import DualActionButtons from '../../../components/action_button/DualActionButtons'
import {
  dialogActionButtonSx,
  dialogActionsWithMetaSx,
  dialogContentWithBorderSx,
  dialogErrorTextSx,
  dialogHeaderWithSubtitleSx,
  dialogSelectedCountSx,
  dialogSubtitleSx,
  dialogTitleSx,
  getDialogCancelAction
} from '../../../constants/dialogSx'
import { UI_COLOR_PRIMARY, UI_COLOR_PRIMARY_HOVER } from '../../../constants/uiColors'
import useRbacInfiniteUsers from '../../hooks/useRbacInfiniteUsers'
import { bulkAssignUserRoles } from '../../services/rbacApi'
import RbacUserSearchField from '../shared/RbacUserSearchField'
import {
  RBAC_BULK_REASSIGN_PAGE_SIZE,
  RBAC_BULK_REASSIGN_SCROLL_LOAD_THRESHOLD_PX,
  RBAC_BULK_REASSIGN_SEARCH_PLACEHOLDER,
  rbacBulkReassignDialogPaperSx,
  rbacBulkReassignSearchFieldSx
} from './constants'
import { buildRbacBulkAssignments, getRbacBulkSubmitErrorMessage } from './helpers'
import RbacBulkReassignUsersList from './RbacBulkReassignUsersList'

export default function RbacBulkReassignRolesDialog({
  open,
  onClose,
  onSuccess,
  roles = [],
  fixedRole = null,
  title = 'Reasignar usuarios en masa',
  description = 'Selecciona los usuarios que quieres reasignar y elige el nuevo rol de cada uno.',
  primaryLabel = 'Reasignar',
  loadingLabel = 'Reasignando...'
}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [roleByUserId, setRoleByUserId] = useState({})
  const [rowErrors, setRowErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const hasFixedRole = Boolean(fixedRole?.id)

  const {
    users,
    loadingUsers,
    loadingMore,
    searchInput,
    setSearchInput,
    loadError,
    setLoadError,
    handleListScroll
  } = useRbacInfiniteUsers({
    open,
    pageSize: RBAC_BULK_REASSIGN_PAGE_SIZE,
    scrollLoadThresholdPx: RBAC_BULK_REASSIGN_SCROLL_LOAD_THRESHOLD_PX
  })

  useEffect(() => {
    if (open) return
    setSelectedIds(new Set())
    setRoleByUserId({})
    setRowErrors({})
  }, [open])

  const selectedCount = selectedIds.size
  const canSubmit =
    selectedCount > 0 && !submitting && !loadingUsers && (hasFixedRole || roles.length > 0)

  const handleClose = () => {
    if (submitting) return
    onClose?.()
  }

  const toggleUser = (userId) => {
    const id = String(userId)
    const willSelect = !selectedIds.has(id)

    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setRoleByUserId((prev) => {
      const next = { ...prev }
      if (willSelect && hasFixedRole) next[id] = [fixedRole.id]
      else delete next[id]
      return next
    })
    setRowErrors((prev) => {
      if (!prev[id]) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const handleRoleChange = (userId, role) => {
    if (hasFixedRole) return

    const id = String(userId)
    setRoleByUserId((prev) => ({
      ...prev,
      [id]: role ? [role.id] : []
    }))
    setRowErrors((prev) => {
      if (!prev[id]) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const handleSubmit = async () => {
    if (!canSubmit) return

    const { assignments, nextErrors } = hasFixedRole
      ? {
          assignments: Array.from(selectedIds).map((userId) => ({
            user_id: Number(userId),
            role_ids: [fixedRole.id]
          })),
          nextErrors: {}
        }
      : buildRbacBulkAssignments(selectedIds, roleByUserId)

    setRowErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    try {
      setSubmitting(true)
      setLoadError('')
      await bulkAssignUserRoles(assignments)
      onSuccess?.(assignments.map((assignment) => assignment.user_id))
      onClose?.()
    } catch (err) {
      setLoadError(getRbacBulkSubmitErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : handleClose}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: rbacBulkReassignDialogPaperSx }}
    >
      <DialogTitle sx={dialogHeaderWithSubtitleSx}>
        <Typography component="span" sx={dialogTitleSx}>
          {title}
        </Typography>
        <Typography sx={dialogSubtitleSx}>{description}</Typography>
      </DialogTitle>
      <DialogContent sx={dialogContentWithBorderSx}>
        <RbacUserSearchField
          value={searchInput}
          onChange={setSearchInput}
          placeholder={RBAC_BULK_REASSIGN_SEARCH_PLACEHOLDER}
          sx={rbacBulkReassignSearchFieldSx}
        />

        {loadError ? <Typography sx={dialogErrorTextSx}>{loadError}</Typography> : null}

        <RbacBulkReassignUsersList
          users={users}
          loadingUsers={loadingUsers}
          loadingMore={loadingMore}
          selectedIds={selectedIds}
          roleByUserId={roleByUserId}
          rowErrors={rowErrors}
          roles={roles}
          fixedRole={fixedRole}
          hasFixedRole={hasFixedRole}
          onToggleUser={toggleUser}
          onRoleChange={handleRoleChange}
          onScroll={handleListScroll}
        />
      </DialogContent>
      <DialogActions sx={dialogActionsWithMetaSx}>
        <Typography sx={dialogSelectedCountSx}>
          {selectedCount} {selectedCount === 1 ? 'usuario seleccionado' : 'usuarios seleccionados'}
        </Typography>
        <DualActionButtons
          fullWidthOnMobile={false}
          minWidth={112}
          gap={1}
          secondaryAction={getDialogCancelAction({
            onClick: handleClose,
            disabled: submitting,
            sx: dialogActionButtonSx
          })}
          primaryAction={{
            label: primaryLabel,
            loadingLabel,
            icon: null,
            color: UI_COLOR_PRIMARY,
            hoverColor: UI_COLOR_PRIMARY_HOVER,
            onClick: handleSubmit,
            disabled: !canSubmit,
            loading: submitting,
            sx: dialogActionButtonSx
          }}
        />
      </DialogActions>
    </Dialog>
  )
}
